import { createClient } from '@/shared/api';
import type { InboxesQuery, InboxesQueryVariables } from '@/shared/api';
import INBOXES_QUERY from '../queries/inboxes';

/**
 * Loads a page of inbox reports together with the recent unread count. Runs the inboxes query with the filter and limit and throws the returned message unless it reports success.
 *
 * @param filter - Filter selecting the inbox reports.
 * @param limit - Maximum number of reports to load; defaults to 10.
 * @returns A promise resolving to the reports and the recent unread count.
 */
const fetchInboxes = async (
  filter: InboxesQueryVariables['filter'],
  limit = 10,
): Promise<{
  reports: NonNullable<InboxesQuery['inboxes']>['reports'];
  recentUnreadCount: NonNullable<InboxesQuery['inboxes']>['recentUnreadCount'];
}> => {
  const client = createClient();
  const inboxesResult = await client.query<InboxesQuery, InboxesQueryVariables>({
    query: INBOXES_QUERY,
    variables: {
      filter,
      limit,
    },
  });
  const { inboxes } = inboxesResult.data;
  // API always returns this field for a resolved query; code already destructured it unconditionally.
  const { isSuccess, message, reports, recentUnreadCount } = inboxes!;
  if (!isSuccess) {
    throw new Error(message ?? undefined);
  }
  return { reports, recentUnreadCount };
};

export default fetchInboxes;
